import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaPause, FaPlay } from 'react-icons/fa';

const ModernAdBanner = ({
  banners = [],
  autoPlay = true,
  interval = 5000,
  showControls = true,
  className = '',
  onBannerClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isPlaying && banners.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, interval);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, interval, banners.length]);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % banners.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);

  const handleBannerClick = (banner) => {
    onBannerClick?.(banner);
    if (banner.link) window.open(banner.link, '_blank');
  };

  if (!banners.length) return null;
  const currentBanner = banners[currentIndex];

  return (
    <div
      className={`relative overflow-hidden rounded-2xl shadow-md bg-gray-50 ${className} h-56 md:h-72 lg:h-80`}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[4000ms] ease-in-out scale-105 hover:scale-110"
        style={{ backgroundImage: `url(${currentBanner.imageUrl})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />

      {/* Content */}
      <div
        className="absolute inset-0 flex items-center justify-start px-6 md:px-10 text-white cursor-pointer"
        onClick={() => handleBannerClick(currentBanner)}
      >
        <div className="max-w-lg">
          {currentBanner.badge && (
            <div className="inline-block mb-2 px-3 py-1 bg-yellow-400 text-gray-900 rounded-full text-xs font-bold uppercase tracking-wide">
              {currentBanner.badge}
            </div>
          )}

          <h2 className="text-xl md:text-3xl font-semibold leading-snug drop-shadow-lg">
            {currentBanner.title}
          </h2>
          {currentBanner.subtitle && (
            <p className="mt-1 text-sm md:text-base text-gray-200">{currentBanner.subtitle}</p>
          )}

          {currentBanner.ctaText && (
            <button
              className="mt-4 bg-white text-gray-900 px-5 py-2 rounded-full text-sm font-semibold hover:bg-gray-200 transition-all"
              onClick={(e) => {
                e.stopPropagation();
                handleBannerClick(currentBanner);
              }}
            >
              {currentBanner.ctaText}
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      {showControls && banners.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all"
          >
            <FaChevronLeft size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-all"
          >
            <FaChevronRight size={14} />
          </button>
        </>
      )}

      {/* Progress Indicators */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300/30">
        <div
          className="h-full bg-yellow-400 transition-all duration-[5000ms] ease-linear"
          key={currentIndex}
          style={{ width: '100%' }}
        />
      </div>

      {/* Play/Pause */}
      {banners.length > 1 && (
        <button
          onClick={() => setIsPlaying((prev) => !prev)}
          className="absolute bottom-3 right-3 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full text-xs"
        >
          {isPlaying ? <FaPause /> : <FaPlay />}
        </button>
      )}
    </div>
  );
};

export default ModernAdBanner;
